// Numbered by lightness (100 lightest -> 900 darkest); only steps we actually use.
const greyScale = {
  grey100: '#F2F2F2',
  grey200: '#E6E6E6',
  grey300: '#CECECE',
  grey400: '#BDBDBD',
  grey500: '#9E9E9E',
  grey550: '#828282',
  grey600: '#757575',
  grey700: '#4F4F4F',
  grey800: '#353535',
  grey900: '#212121'
};

const theme = {
  colours: {
    ...greyScale,
    bgGrey: greyScale.grey100,
    border: greyScale.grey200,
    neutralText: greyScale.grey700,
    mutedText: greyScale.grey600,
    slate: greyScale.grey900,
    red: '#ED2924',
    nimbleRed: '#DB1A1A',
    black: '#1E1E1E',
    white: '#FFFFFF',
    orange: '#ED2924',
    pink: '#FBD4CF',
    lightPink: '#FDE9E9',
    green: '#007A68',
    yellow: '#F7ED00',
    default: { light: '#595959', dark: '#1E1E1E', hover: '#D8D8D8' }, // text color
    primary: { light: '#EA2C2E', dark: '#e6e6e6', shallow: '#F8A99F' }, // red
    cream: '#FFFCF5',
    darkGreen: '#017A68',
    lightGreen: '#D9EBE8',
    // Translucent tokens: these sit over images and tinted panels, so they
    // cannot be swapped for the opaque greyScale values.
    mutedOnLight: 'rgba(0, 0, 0, 0.6)',
    mutedOnDark: 'rgba(255, 255, 255, 0.6)',
    hairline: 'rgba(0, 0, 0, 0.05)',
    inputBorder: 'rgba(0, 0, 0, 0.3)',
    heroScrim: 'rgba(0, 0, 0, 0.25)'
  },

  font: {
    nimbleFont: 'SharpGroteskBook, SharpGroteskBook-fallback, sans-serif',
    nimbleMediumFont:
      'SharpGroteskMedium, SharpGroteskMedium-fallback, sans-serif',
    nimbleHeadingFont:
      'SharpGroteskBold, SharpGroteskBold-fallback, sans-serif',
    nimbleBoldFont:
      'SharpGroteskSmBold, SharpGroteskSmBold-fallback, sans-serif'
  },

  // Knowledge hub heading scale, keyed by the Figma style names.
  typography: {
    h1Display: { fontSize: '48px', lineHeight: '56px', fontWeight: 500 },
    h1Bold: { fontSize: '35px', lineHeight: '45px', fontWeight: 500 },
    h2Bold: { fontSize: '28px', lineHeight: '38px', fontWeight: 500 },
    h3Bold: { fontSize: '20px', lineHeight: '28px', fontWeight: 500 },
    headingMd: { fontSize: '24px', lineHeight: '120%', fontWeight: 500 }
  },

  // Applies a heading token from typography, on the brand Medium face.
  // Usage: ${props => props.theme.typeStyle('h2Bold')}
  typeStyle: token => {
    const style = theme.typography[token];
    if (!style) {
      return null;
    }
    return `
      font-family: ${theme.font.nimbleMediumFont};
      font-size: ${style.fontSize};
      font-weight: ${style.fontWeight};
      line-height: ${style.lineHeight};
    `;
  },

  sectionPadding: {
    sectionPaddingTbDesktop: '70px',
    sectionPaddingTbMobile: '50px',
    desktopPaddingLr: '40px',
    mobilePaddingLr: '15px'
  },
  sectionMargin: {
    headerMarginXPhone: '15px',
    headerMarginXDesktop: '30px'
  },
  screens: {
    screenLarge: '960px',
    screenMedium: '767px'
  },
  components: {
    componentStandardWidthMax: '1440px',
    phoneWidthMax: '800px',
    desktopWidthMin: '801px',
    dotSize: '9px',
    maxWidth: '1440px'
  },
  mediaBreakpoints: {
    desktop: '(min-width: 1440px)',
    tablet: '(min-width: 480px) and (max-width: 1439px)',
    mobile: '(max-width: 1239px)',
    smallScreen: '(max-width: 479px)'
  },
  layout: {
    maxWidth: '1403px', // standard site container
    narrowMaxWidth: '1280px', // narrower content width (hub landing, pagination)
    gutter: {
      lg: '60px', // desktop side inset
      md: '40px', // tablet (<= breakpoints.max.md)
      sm: '20px' // mobile (<= breakpoints.max.sm)
    }
  },
  breakpoints: {
    // new
    max: {
      xs: '400px',
      sm: '550px',
      md: '767px',
      lg: '991px',
      xl: '1199px'
    },
    min: {
      xs: '401px',
      sm: '551px',
      md: '768px',
      lg: '992px',
      xl: '1200px'
    }
  },
  misc: {
    mobileMenuTrigger: '990px',
    textShadow: '0 0 3px #E3367F',
    cardRadius: '0px',
    buttonRadius: '4px',
    sideMenuWidth: '270px',
    panelRadius: '20px',
    starEmptyColor: '#F8A99F',
    starFillColor: '#ED2924'
  },
  size: {
    small: '12px',
    medium: '14px',
    large: '18px',
    xlarge: '21px',
    xxlarge: '24px',
    giant: '32px',
    xgiant: '36px',
    xxgiant: '56px'
  },
  transition: {
    transition300ms: {
      transition: 'all .3s ease-in-out',
      '-webkit-transition': 'all .3s ease-in-out',
      '-o-transition': 'all .3s ease-in-out'
    }
  }
};

export { theme };
